/*
Author: Chathuraka Mallawa Arachchi
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import { useEffect, useState } from 'react'
import Select, { components } from 'react-select'
import {
    DropDownOption,
    nodeConfigurations,
} from '../survey/SurveyModel/types/nodeTypes'
import Errors from './Error'

const DropDown = (props: any) => {
    const {
        options,
        name,
        form,
        value,
        handleChange,
        excludeOptions,
        propDivClasses = '',
        isInValid = true,
        onBlur,
        disabled,
        configurations,
        isSearchable = false,
        onInputChange,
        inputValue,
        placeholder,
        renderValue,
    } = props
    const [optionList, setOptionList] = useState<DropDownOption[]>(options)
    //const [textValue, setTextValue] = React.useState<string> ();
    const [isFocused, setIsFocused] = useState(false)
    const [isActive, setIsActive] = useState(false)
    // workaround to make sure backwards compatibility with existing implementations
    const [focusedOption, setFocusedOption] = useState<DropDownOption | null>(null)

    const prepareTypeOptions = () => {
        let newoptionList: DropDownOption[] = []
        if (excludeOptions.length) {
            newoptionList = options.filter((rowItem: DropDownOption) => {
                return !excludeOptions.some(
                    (item: DropDownOption) =>
                        item.value === rowItem.value && item.value !== value
                )
            })
        }

        setOptionList(newoptionList)
    }
    useEffect(() => {
        if (options?.length) {
            setOptionList(options)
        }
    }, [options, excludeOptions])

    useEffect(() => {
        if (excludeOptions && excludeOptions?.length && options.length) {
            prepareTypeOptions()
        }
    }, [excludeOptions, options])

    const handleOnBlure = () => {
        onBlur()
        setIsFocused(false)
    }

    const formatOptionLabel = (
        option: DropDownOption,
        { context }: any
    ) => {
        const { label, description, icon, component, value } = option;

        if (component && context === 'menu') {
            const resolvedComponent = typeof component === 'function' ? component(value === focusedOption?.value) : component
            return <>{resolvedComponent}</>
        }

        const resolvedIcon = icon ? (typeof icon === 'function' ? icon(context === 'menu' ? focusedOption?.label === label : false) : icon) : null
        const iconEl = icon
            ? <span style={{ marginRight: 8, flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>{resolvedIcon}</span>
            : null

        return context === 'menu' ? (
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                {iconEl}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div>{label}</div>
                    {description && <div>{description}</div>}
                </div>
            </div>
        ) : (
            renderValue ? (
                <>{renderValue(option)}</>
            ) : (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {iconEl}
                    <span>{label}</span>
                </div>
            )
        )
    }

    const CustomOption = (props: any) => {
        const { isFocused, data } = props;

        if (isFocused) {
            setFocusedOption(data);
        }
        // ❗ Important: render the default Option exactly as-is
        return <components.Option {...props} />;
    };

    return (
        <div className={`${isInValid ? 'error' : ''}`}>
            <div
                className={`${propDivClasses} input-div ${configurations?.some((config: nodeConfigurations) => config.key === 'FieldWidthPct' && config.value === '0.5') ? 'width-half' : ''} ${isFocused ? 'input-div-focus' : ''} ${isActive ? 'input-div-active' : ''}`}
            >
                <Select
                    className={`
            custom-select
            ${configurations?.some((config: nodeConfigurations) => config.key === 'FieldWidthPct' && config.value === '0.5') ? 'custom-select-lg' : ''}
            ${disabled ? 'disabled' : ''}`}
                    name={name}
                    placeholder={`${disabled ? '' : (placeholder || 'Select')}`}
                    options={optionList}
                    onBlur={handleOnBlure}
                    onChange={handleChange}
                    value={value || ''}
                    
                    classNamePrefix="custom-select-basic"
                    onMenuOpen={() => setIsActive(true)}
                    onMenuClose={() => setIsActive(false)}
                    isDisabled={disabled}
                    isSearchable={isSearchable}
                    onInputChange={isSearchable ? onInputChange : undefined}
                    inputValue={isSearchable ? inputValue : undefined}
                    components={{
                        IndicatorSeparator: () => null,
                        Option: CustomOption,
                    }}
                    formatOptionLabel={formatOptionLabel}
                    styles={{
                        control: (baseStyles, state) => ({
                            ...baseStyles,
                            borderColor: state.isFocused
                                ? 'var(--comp-field-bordercolor-default)'
                                : 'var(--comp-field-bordercolor-default)',

                            borderRadius: '0',
                            boxShadow: 'none', // Remove the blue border (box-shadow)
                            ':hover': {
                                borderColor:
                                    'var(--comp-field-bordercolor-default)', // Change border color on hover
                            },
                            fontSize:
                                'var(--comp-field-fontsize-input-default)',
                            fontWeight:
                                'var(--comp-field-fontweight-input-default)',
                            color: 'var(--comp-field-fontcolour-default)',
                            fontFamily: '$Lexend',
                            height: 'var(--input-height)',
                            minHeight: 'var(--input-height)',
                        }),
                        dropdownIndicator: (provided: any, state: any) => ({
                            ...provided,
                            padding: '8px 6px 8px 0',
                            margin: 0,
                            lineHeight: 0,
                            color: state.isFocused
                                ? 'var(--comp-field-fontcolour-default)'
                                : 'var(--comp-icon-color-positive)', // Change arrow color
                            ':hover': {
                                color: 'darkblue', // Change color on hover
                            },
                            display: disabled ? 'none' : 'flex',
                        }),
                        indicatorsContainer: (provided: any) => ({
                            ...provided,
                            padding: 0,
                            margin: 0,
                        }),
                        menu: (provided: any) => ({
                            ...provided,
                            borderRadius: 0, // Remove border radius for the dropdown menu
                            margin: 0, // Remove margin around the menu
                            padding: '0px 0px',
                            width: `calc(100% + 4px)`,
                            left: '-2px',
                            borderTop: `${isInValid ? '3px solid var(--comp-field-bordercolor-error' : '3px solid var(--comp-field-bordercolor-focus'})`,
                        }),
                        menuList: (provided) => ({
                            ...provided,
                            height: 'auto',
                            overflow: 'auto',
                            paddingTop: '0px',
                            paddingBottom: '0px',
                        }),
                        valueContainer: (baseStyles, props) => {
                            const hasIcon = props.hasValue && props.getValue()[0]?.icon;
                            return {
                                ...baseStyles,
                                paddingRight: hasIcon ? '0px' : baseStyles.paddingRight,
                                //padding: 'var(--comp-field-padding-fieldpadding)',
                                height: '36px',
                            };
                        },
                        singleValue: (baseStyles) => ({
                            ...baseStyles,
                            fontSize:
                                'var(--comp-field-fontsize-input-default)',
                            fontWeight:
                                'var(--comp-field-fontweight-input-default',
                            color: 'var(--comp-field-fontcolour-default)',
                        }),
                        placeholder: (baseStyles) => ({
                            ...baseStyles,
                            fontSize:
                                'var(--comp-field-fontsize-input-default)',
                            fontWeight:
                                'var(--comp-field-fontweight-input-default',
                            color: 'var(--comp-field-fontcolour-default)',
                        }),
                        option: (baseStyles, state) => ({
                            ...baseStyles,
                            color: state.isSelected
                                ? state.isFocused
                                    ? '#fff'
                                    : 'var(--comp-field-fontcolour-input-default)'
                                : state.isFocused
                                  ? '#fff'
                                  : 'var(--comp-field-fontcolour-input-default)', // Font color for options
                            backgroundColor: state.isFocused
                                ? 'var(--comp-field-bgcolor-menuoptionactive)'
                                : 'var(--comp-field-fontcolor-menu-active)', // Background for focused options
                            fontSize: '14px', // Font size for dropdown items
                            padding: 'var(--comp-field-padding-gap)',
                        }),
                    }}
                >
                    {/* <option value="" disabled>
                
            </option>
            {optionList.map((option: DropDownOption, i: number) => (
                <option key={i} value={option.value}>
                    {option.name}
                </option>
            ))} */}
                </Select>
            </div>
            {form ? (
                <Errors
                    fieldName={name}
                    validation={form[name] ? form[name] : undefined}
                />
            ) : (
                ''
            )}
        </div>
    )
}
export default DropDown
